import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, message, Input } from "antd";
import * as api from "../../../utils/api";
import { PageLayout } from "../../../components/page-layout";
import { CreateStoreModal } from "../../../components/create-store-modal";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import * as S from "./styles";
import ExcelToJsonConverter from "../../../components/excel-to-json";

const { Search } = Input;

const DEFAULT_ERROR_MESSAGE =
  "Erro inesperado. Por favor, tente novamente mais tarde.";

function getDBColumnName(column) {
  switch (column) {
    case "CONCESSIONÁRIA":
      return "title";

    case "CNPJ":
      return "cnpj";

    case "SENHA":
      return "password";

    case "BAIRRO":
      return "neighborhood";

    case "CEP":
      return "postal_code";

    case "CIDADE":
      return "city";

    case "DDD":
      return "area_code";

    case "ENDEREÇO":
      return "address";

    case "ESTADO":
      return "state";

    case "TELEFONE":
      return "phone_number";

    default:
      return "NOT_FOUND";
  }
}

export const StoresPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [storesDisplay, setStoresDisplay] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const columns = [
    {
      title: "Concessionária",
      key: "title",
      dataIndex: "title",
    },
    {
      title: "Ações",
      key: "action",
      render: (_, { id }) => (
        <S.ActionsColumnContainer>
          <span onClick={() => navigate(`/concessionarias/${id}`)}>
            Ver Detalhes
          </span>
          <span
            onClick={() => {
              setSelectedStore(stores.find((i) => i.id === id));
              setOpenRemove(true);
            }}
          >
            Remover
          </span>
        </S.ActionsColumnContainer>
      ),
    },
  ];

  const handleGetStores = async (searchTerm = "") => {
    setLoading(true);

    try {
      const { result, error } = await api.post("/get-stores", { searchTerm });

      if (error) {
        throw new Error(error);
      }

      const mapped = result?.map((i, index) => ({ ...i, key: index })) || [];

      setStores(mapped);
      setStoresDisplay(mapped);
    } catch (err) {
      message.error(err.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async (payload) => {
    try {
      const { error } = await api.post("/create-store", {
        ...payload,
        cnpj: payload.cnpj.replace(/[^\d]+/g, ""),
      });

      if (error) {
        throw new Error(error);
      }

      setOpenCreate(false);
      await handleGetStores();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleRemoveStore = async (e) => {
    e.preventDefault();

    try {
      const { error } = await api.post("/remove-store", {
        storeId: selectedStore.id,
      });

      if (error) {
        throw new Error(error);
      }

      setOpenRemove(false);
      await handleGetStores();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setSelectedStore(null);
    }
  };

  const onSearch = async (value, _e, info) => {
    if (info.source === "clear") {
      handleGetStores();
      return null;
    }

    await handleGetStores(value);
  };

  const handleCreateManyStores = async (stores) => {
    try {
      if (!stores || !Array.isArray(stores) || !stores.length) {
        return null;
      }

      const [header, ...rest] = stores;

      const mappedStores = rest.map((row) => {
        const store = {};

        Object.entries(header).forEach(([key, value]) => {
          const dbColumnName = getDBColumnName(value);

          if (row[key]) {
            store[dbColumnName] = row[key];
          } else {
            store[dbColumnName] = "";
          }
        });

        return store;
      });

      const result = await api.post("/create-many-stores", {
        stores: mappedStores,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      message.success("Concessionárias registradas com sucesso.");
      await handleGetStores();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    handleGetStores();
  }, []);

  return (
    <>
      <PageLayout>
        <S.Container>
          <div id="search-container">
            <Search
              placeholder="Buscar por nome ou CNPJ"
              allowClear
              onSearch={onSearch}
            />
          </div>
          <div id="action-container">
            <ExcelToJsonConverter
              handleUpload={handleCreateManyStores}
              disabled={true}
            />
            <Button
              type="primary"
              onClick={() => setOpenCreate(true)}
              id="app-button"
            >
              Registrar
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={storesDisplay}
            size="small"
            bordered={true}
            loading={loading}
            pagination={{
              defaultPageSize: 15,
            }}
          />
        </S.Container>
      </PageLayout>
      <CreateStoreModal
        open={openCreate}
        onCancel={() => setOpenCreate(false)}
        onSubmit={handleCreateStore}
      />
      <ConfirmationModal
        open={openRemove}
        onCancel={() => {
          setSelectedStore(null);
          setOpenRemove(false);
        }}
        onOk={handleRemoveStore}
        selectedStore={selectedStore}
      />
    </>
  );
};
